// Loyalty system type definitions following WCAG AA accessibility standards

export type AchievementLevel = 'explorer' | 'hunter' | 'champion' | 'diamond'
export type UserLevel = AchievementLevel | null // null = no achievement unlocked yet

export interface PhysicalReward {
  id: string
  name: string
  description: string
  type: 'product' | 'discount' | 'access'
  value: string
}

export interface Achievement {
  id: string
  level: AchievementLevel
  name: string
  description: string
  pointsRequired: number
  unlockedAt?: Date
  isUnlocked: boolean
  // Accessibility properties
  ariaLabel: string
  srDescription: string // Screen reader description
  // Visual properties
  frameColor: string
  frameStyle: string
  iconComponent: string
  celebrationMessage: string
  // Reward properties
  physicalRewards: PhysicalReward[]
  rewardsExpireAt?: Date
  canClaimPhysicalRewards: boolean
  // Time-based eligibility
  unlockedWithinEligibilityWindow?: boolean // True if unlocked within 1 year of registration
  rewardType: 'frame_and_rewards' | 'frame_only' // Determines what user gets
}

export interface PointsTransaction {
  id: string
  userId: string
  amount: number
  type: 'purchase' | 'bonus' | 'admin_adjustment' | 'achievement_bonus'
  source: {
    raffleId?: string
    ticketIds?: string[]
    adminNote?: string
  }
  description: string
  createdAt: Date
  metadata?: Record<string, unknown>
}

export interface UserLoyalty {
  userId: string
  totalPoints: number
  currentLevel: UserLevel // null if no achievements unlocked
  totalPurchases: number
  achievements: Achievement[]
  pointsHistory: PointsTransaction[]
  pointsToNextLevel?: number
  progressPercentage?: number
  joinedAt: Date // User registration date
  rewardEligibilityExpiresAt: Date // 1 year from registration
  isWithinRewardEligibilityWindow: boolean // True if still within 1 year
  lastUpdated?: Date
  totalSpent?: number
}

export interface ActiveBonus {
  raffleId: string
  bonusPercentage: number
  reason: string
  appliedAt: Date
  expiresAt?: Date
}

// Achievement configuration with rewards
export const ACHIEVEMENT_LEVELS: Record<AchievementLevel, Omit<Achievement, 'id' | 'unlockedAt' | 'isUnlocked'>> = {
  explorer: {
    level: 'explorer',
    name: 'Explorador',
    description: 'Has comenzado tu aventura en Wild Draw',
    frameColor: '#F59E0B',
    frameStyle: 'solid',
    iconComponent: 'Fish',
    celebrationMessage: '¡Bienvenido a Wild Draw! Has desbloqueado tu primer nivel y tus primeras recompensas.',
    pointsRequired: 500,
    ariaLabel: 'Nivel Explorador desbloqueado',
    srDescription: 'Nivel inicial para todos los usuarios nuevos',
    physicalRewards: [
      {
        id: 'explorer_cebado',
        name: 'Bolsa de Cebado 5kg',
        description: 'Mezcla premium para iniciar tu aventura',
        type: 'product',
        value: '5kg'
      },
      {
        id: 'explorer_shipping',
        name: 'Envío Gratis',
        description: 'Sin costos de envío para tu primer kit',
        type: 'discount',
        value: '100%'
      },
      {
        id: 'explorer_frame',
        name: 'Marco Dorado Básico',
        description: 'Muestra tu nivel en tu perfil',
        type: 'access',
        value: 'Marco Visual'
      }
    ],
    canClaimPhysicalRewards: true,
    rewardType: 'frame_and_rewards'
  },
  hunter: {
    level: 'hunter',
    name: 'Cazador',
    description: 'Tu destreza en las aguas te convierte en un verdadero cazador',
    frameColor: '#6B7280',
    frameStyle: 'solid',
    iconComponent: 'Target',
    celebrationMessage: '¡Excelente! Has alcanzado el nivel Cazador y desbloqueado la gama completa de cebos.',
    pointsRequired: 1000,
    ariaLabel: 'Nivel Cazador desbloqueado',
    srDescription: 'Nivel intermedio que requiere 500 puntos',
    physicalRewards: [
      {
        id: 'hunter_cebos_1kg',
        name: 'Bolsa de Cebos 1kg',
        description: 'Mezcla especializada para cazadores expertos',
        type: 'product',
        value: '1kg'
      },
      {
        id: 'hunter_popup',
        name: 'Pop-up Premium',
        description: 'Boilies flotantes de alta calidad',
        type: 'product',
        value: '1_jar'
      },
      {
        id: 'hunter_fluo_popup',
        name: 'Fluo Pop-up',
        description: 'Pop-ups fluorescentes para pesca nocturna',
        type: 'product',
        value: '1_jar'
      },
      {
        id: 'hunter_potenciador',
        name: 'Potenciador Nutricional',
        description: 'Aditivo profesional para maximizar la atracción',
        type: 'product',
        value: '250ml'
      },
      {
        id: 'hunter_frame',
        name: 'Marco Plateado',
        description: 'Marco elegante que refleja tu experiencia',
        type: 'access',
        value: 'Marco Plateado'
      }
    ],
    canClaimPhysicalRewards: false,
    rewardType: 'frame_only'
  },
  champion: {
    level: 'champion',
    name: 'Campeón',
    description: 'Maestro de las técnicas más avanzadas, un verdadero Wild',
    frameColor: '#EAB308',
    frameStyle: 'solid',
    iconComponent: 'Crown',
    celebrationMessage: '¡Increíble! Has conseguido el nivel Campeón y el kit completo de maestro.',
    pointsRequired: 1500,
    ariaLabel: 'Nivel Campeón desbloqueado',
    srDescription: 'Nivel avanzado que requiere 2000 puntos',
    physicalRewards: [
      {
        id: 'champion_explorer_kit',
        name: 'Kit Explorer Completo',
        description: 'Bolsa de cebado 5kg + envío gratis',
        type: 'product',
        value: 'explorer_complete'
      },
      {
        id: 'champion_hunter_kit',
        name: 'Kit Hunter Completo',
        description: 'Gama completa: cebos 1kg + pop-up + fluo pop-up + potenciador',
        type: 'product',
        value: 'hunter_complete'
      },
      {
        id: 'champion_frame',
        name: 'Marco Dorado Premium',
        description: 'Marco dorado exclusivo para campeones',
        type: 'access',
        value: 'Marco Dorado Premium'
      }
    ],
    canClaimPhysicalRewards: false,
    rewardType: 'frame_only'
  },
  diamond: {
    level: 'diamond',
    name: 'Diamante',
    description: 'Élite Wild: dominas todos los secretos de la pesca carpista',
    frameColor: '#06B6D4',
    frameStyle: 'premium',
    iconComponent: 'Gem',
    celebrationMessage: '¡LEGENDARIO! Has alcanzado el nivel máximo: Diamante y el acceso VIP exclusivo.',
    pointsRequired: 2000,
    ariaLabel: 'Nivel Diamante desbloqueado',
    srDescription: 'Nivel máximo que requiere 5000 puntos',
    physicalRewards: [
      {
        id: 'diamond_complete_collection',
        name: 'Colección Completa',
        description: 'Todos los kits Explorer + Hunter + Champion',
        type: 'product',
        value: 'complete_collection'
      },
      {
        id: 'diamond_exclusive_raffles',
        name: 'Sorteos Exclusivos de Crafting',
        description: 'Acceso a sorteos premium solo para miembros Diamante',
        type: 'access',
        value: 'exclusive_crafting_raffles'
      },
      {
        id: 'diamond_frame',
        name: 'Marco de Diamante',
        description: 'El marco más exclusivo y prestigioso',
        type: 'access',
        value: 'Marco de Diamante'
      },
      {
        id: 'diamond_vip_support',
        name: 'Soporte VIP',
        description: 'Atención prioritaria y personalizada',
        type: 'access',
        value: 'vip_support'
      }
    ],
    canClaimPhysicalRewards: false,
    rewardType: 'frame_only'
  }
}

export function getAchievementByPoints(points: number): UserLevel {
  // Find the highest achievement level the user has enough points for
  let highestLevel: UserLevel = null // NO level by default

  for (const [level, data] of Object.entries(ACHIEVEMENT_LEVELS)) {
    if (points >= data.pointsRequired) {
      highestLevel = level as AchievementLevel
    }
  }

  return highestLevel // Returns null if user has < 500 points
}

export function calculatePointsToNextLevel(currentPoints: number): number {
  const currentLevel = getAchievementByPoints(currentPoints)

  // If no level achieved yet, points needed for first level (explorer)
  if (currentLevel === null) {
    return ACHIEVEMENT_LEVELS.explorer.pointsRequired - currentPoints
  }

  const levels = Object.keys(ACHIEVEMENT_LEVELS) as AchievementLevel[]
  const currentIndex = levels.indexOf(currentLevel)

  if (currentIndex === levels.length - 1) {
    return 0 // Already at max level
  }

  const nextLevel = levels[currentIndex + 1]
  const nextLevelPoints = ACHIEVEMENT_LEVELS[nextLevel].pointsRequired

  return nextLevelPoints - currentPoints
}

export function calculateProgressPercentage(currentPoints: number): number {
  const currentLevel = getAchievementByPoints(currentPoints)

  // If no level achieved yet, progress towards first level (explorer)
  if (currentLevel === null) {
    return Math.round((currentPoints / ACHIEVEMENT_LEVELS.explorer.pointsRequired) * 100)
  }

  const levels = Object.keys(ACHIEVEMENT_LEVELS) as AchievementLevel[]
  const currentIndex = levels.indexOf(currentLevel)

  if (currentIndex === levels.length - 1) {
    return 100 // Max level reached
  }

  const currentLevelPoints = ACHIEVEMENT_LEVELS[currentLevel].pointsRequired
  const nextLevel = levels[currentIndex + 1]
  const nextLevelPoints = ACHIEVEMENT_LEVELS[nextLevel].pointsRequired

  const pointsInCurrentLevel = currentPoints - currentLevelPoints
  const pointsNeededForNext = nextLevelPoints - currentLevelPoints

  return Math.round((pointsInCurrentLevel / pointsNeededForNext) * 100)
}

// Helper functions for loyalty announcements
export function getAchievementAnnouncementText(level: AchievementLevel): string {
  return ACHIEVEMENT_LEVELS[level].celebrationMessage
}

export function getPointsAnnouncementText(points: number): string {
  if (points >= 1000) {
    return `¡Increíble! Has ganado ${points} puntos de lealtad`
  } else if (points >= 100) {
    return `¡Excelente! Has ganado ${points} puntos`
  } else {
    return `Has ganado ${points} puntos`
  }
}

// Helper functions for time-based reward eligibility
export function calculateRewardEligibilityExpiration(registrationDate: Date): Date {
  const expirationDate = new Date(registrationDate)
  expirationDate.setFullYear(expirationDate.getFullYear() + 1)
  return expirationDate
}

export function isWithinRewardEligibilityWindow(registrationDate: Date): boolean {
  const now = new Date()
  const eligibilityExpiration = calculateRewardEligibilityExpiration(registrationDate)
  return now <= eligibilityExpiration
}

export function getTimeRemainingInEligibilityWindow(registrationDate: Date): {
  days: number
  hours: number
  minutes: number
  expired: boolean
} {
  const now = new Date()
  const eligibilityExpiration = calculateRewardEligibilityExpiration(registrationDate)
  const timeDiff = eligibilityExpiration.getTime() - now.getTime()

  if (timeDiff <= 0) {
    return { days: 0, hours: 0, minutes: 0, expired: true }
  }

  const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60))

  return { days, hours, minutes, expired: false }
}

export function determineAchievementRewardType(
  unlockedAt: Date,
  registrationDate: Date
): 'frame_and_rewards' | 'frame_only' {
  const eligibilityExpiration = calculateRewardEligibilityExpiration(registrationDate)
  return unlockedAt <= eligibilityExpiration ? 'frame_and_rewards' : 'frame_only'
}